import React, { useState, useEffect } from 'react';
import {
  searchFromDatabase,
  getApiUsage,
  canCallApi,
  incrementApiCounter,
} from '../services/restaurantSearchService';
import {
  generateSessionToken,
  autocomplete,
  getPlaceDetails,
} from '../services/googlePlacesService';

const RestaurantSearch = ({ map, onPlaceSelect, bounds, cityName, user }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiUsage, setApiUsage] = useState({ 
    callCount: 0, 
    dailyLimit: 300, 
    remainingCalls: 300 
  });

  useEffect(() => {
    loadApiUsage();
  }, []);

  const loadApiUsage = async () => {
    try {
      const usage = await getApiUsage();
      setApiUsage(usage);
    } catch (error) {
      console.error('API 사용량 로드 실패:', error);
    }
  };

  // ⭐ bounds 내 위치인지 확인 헬퍼
  const isWithinBounds = (lat, lng) => {
    if (!bounds) return true;
    return lat <= bounds.north && lat >= bounds.south && 
           lng <= bounds.east && lng >= bounds.west;
  };

  const handleSearch = async () => {
    if (!user) {
      alert("로그인 후 검색할 수 있습니다.");
      return;
    }
    if (!query.trim()) {
      alert('검색어를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      console.log('🔍 검색 시작:', query, '도시:', cityName);
      
      // 1단계: DB 검색 (bounds 무관)
      const dbResult = await searchFromDatabase(query, cityName);

      // if (dbResult.places && dbResult.places.length > 0) {
      //   console.log(`✅ DB에서 ${dbResult.places.length}개 찾음`);
      //   const place = dbResult.places[0];
      //   // ⭐ DB 결과도 bounds 체크
      //   if (bounds && !isWithinBounds(place.lat, place.lng)) {
      //     alert(`${cityName} 내 맛집만 표시됩니다.`);
      //     setLoading(false);
      //     return;
      //   }
      //   onPlaceSelect(place);
      //   setQuery('');
      //   setLoading(false);
      //   return;
      // }

      if (dbResult.places && dbResult.places.length > 0) {
        console.log(`✅ DB에서 ${dbResult.places.length}개 찾음`);
        
        // bounds 내 결과만 필터링
        const filteredPlaces = bounds
          ? dbResult.places.filter(p => isWithinBounds(p.lat, p.lng))
          : dbResult.places;

        if (filteredPlaces.length === 0) {
          alert(`${cityName} 내 검색 결과가 없습니다.`);
          setLoading(false);
          return;
        }

        // ⭐ 여러 개 전달
        onPlaceSelect(filteredPlaces);
        setQuery('');
        setLoading(false);
        return;
      }


      // 2단계: API 제한 체크
      const apiCheck = await canCallApi();

      if (!apiCheck.canCall) {
        alert(`오늘의 신규 검색 한도(${apiCheck.limit}회)를 모두 사용했습니다.`);
        setLoading(false);
        return;
      }

      // 3단계: Google API 호출 (⭐ bounds 전달)
      const sessionToken = generateSessionToken();
      
      // ⭐ bounds가 있으면 locationBias로 약하게 적용 (strictBounds: false)
      const autocompleteOptions = bounds ? {
        bounds: bounds,
        strictBounds: false,  // ✅ 엄격 차단 OFF [web:26]
      } : {};

      const suggestions = await autocomplete(query, sessionToken, autocompleteOptions);

      if (suggestions.length === 0) {
        alert('검색 결과가 없습니다.');
        setLoading(false);
        return;
      }

      const placeId = suggestions[0].placePrediction.placeId;
      const placeDetails = await getPlaceDetails(placeId, sessionToken);

     

      // 4단계: 카운터 증가
      const updatedCounter = await incrementApiCounter();
      setApiUsage({
        callCount: updatedCounter.callCount,
        dailyLimit: updatedCounter.dailyLimit,
        remainingCalls: updatedCounter.remainingCalls,
      });

      if (updatedCounter.remainingCalls <= 10 && updatedCounter.remainingCalls > 0) {
        alert(`⚠️ 오늘 남은 검색 횟수: ${updatedCounter.remainingCalls}회`);
      }

      // 검색 결과 표시 ✅ bounds 내 확인됨
      //console.log('✅ bounds 내 검색 성공:', placeDetails.name);
      //onPlaceSelect(placeDetails);
      console.log('✅ bounds 내 검색 성공:', placeDetails.displayName?.text || placeDetails.name);
      onPlaceSelect({
        name: placeDetails.displayName?.text || placeDetails.name,
        address: placeDetails.formattedAddress,
        lat: placeDetails.location?.latitude,
        lng: placeDetails.location?.longitude,
        placeId: placeDetails.id,  // ✅ 핵심! id → placeId로 명시
      });
      setQuery('');

    } catch (error) {
      console.error('❌ 검색 오류:', error);
      alert(`검색 실패: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      {/* 검색창 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 15px',
      }}>
        <span style={{ fontSize: '20px', color: '#666', marginRight: '10px' }}>🔍</span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          placeholder={user ? `"${cityName}"에서 맛집 검색...` : "로그인 후 검색할 수 있습니다"}
          disabled={loading}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            padding: '15px 0',
            fontSize: '16px',
            background: 'transparent',
          }}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            background: loading ? '#ccc' : '#4285f4',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 15px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
          }}
        >
          {loading ? '검색 중...' : '검색'}
        </button>
      </div>

      {/* API 사용량 + 도시 정보 */}
      <div style={{
        padding: '8px 15px',
        background: '#f0f8ff',
        borderTop: '1px solid #e0e0e0',
        fontSize: '12px',
        color: '#666',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        gap: '2px',
      }}>
        <span>오늘 검색: {apiUsage.callCount}/{apiUsage.dailyLimit}회</span>
        <span style={{ 
          color: apiUsage.remainingCalls <= 10 ? '#ff4444' : '#666',
          fontWeight: apiUsage.remainingCalls <= 10 ? 'bold' : 'normal'
        }}>
          남은 횟수: {apiUsage.remainingCalls}회
        </span>
        {cityName && (
          <small style={{ color: '#888', fontSize: '11px' }}>
            📍 {cityName} 검색 제한
          </small>
        )}
      </div>
    </div>
  );
};

export default RestaurantSearch;
